/**
 * Cost Calculator Page
 * P2 REFACTORED: Split into smaller components for better maintainability
 * Components: AssumptionsBar, CostSummaryCards, CostCharts, CostDetailsTable, CostDetailModal
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import apiClient from "@/lib/api";
import { useCompanyFilter } from "@/contexts/CompanyFilterContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calculator, RefreshCw } from "lucide-react";

// Extracted components
import {
  AssumptionsBar,
  CostSummaryCards,
  CostCharts,
  CostDetailsTable,
  CostDetailModal
} from "@/components/calculator";

const CostCalculatorPage = () => {
  const { selectedCompany, getApiCompanyId } = useCompanyFilter();
  const [loading, setLoading] = useState(true);
  const [payRules, setPayRules] = useState([]);
  const [wageLaws, setWageLaws] = useState([]);

  // Assumptions
  const [hoursPerYear, setHoursPerYear] = useState(2080);
  const [hourlyRate, setHourlyRate] = useState(25);
  const [overtimePercent, setOvertimePercent] = useState(10);
  const [period, setPeriod] = useState("annual");
  
  // Sorting - default to Cost descending
  const [sortConfig, setSortConfig] = useState({ key: "amount", direction: "desc" });
  
  // Detail modal state
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailPeriod, setDetailPeriod] = useState("annual");

  // Cost Details filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Sort handler
  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const companyId = getApiCompanyId();
      const [rulesRes, lawsRes] = await Promise.all([
        apiClient.get(`/api/pay-rules/summary`, { 
          params: companyId ? { company_id: companyId } : {}
        }),
        apiClient.get(`/api/wage-laws`)
      ]);
      setPayRules(rulesRes.data.rules || []);
      setWageLaws(lawsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [getApiCompanyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, selectedCompany]);

  // Build wage laws lookup by state and violation type
  const wageLawsLookup = useMemo(() => {
    const lookup = {};
    wageLaws.forEach(law => {
      const key = `${law.StateName}-${law.ViolationType}`;
      lookup[key] = law;
    });
    return lookup;
  }, [wageLaws]);

  // Get matching wage law for a rule
  const getMatchingLaw = useCallback((state, category, code) => {
    let violationType = null;
    const codeUpper = (code || '').toUpperCase();
    
    if (codeUpper.includes('REST')) violationType = 'Rest Break';
    else if (codeUpper.includes('MEAL')) violationType = 'Meal Break';
    else if (codeUpper.includes('OT') || codeUpper.includes('OVERTIME')) violationType = 'Overtime';
    else if (codeUpper.includes('DOUBLE')) violationType = 'DoubleTime';
    else {
      if (category?.includes('Overtime')) violationType = 'Overtime';
      else if (category?.includes('Meal')) violationType = 'Meal Break';
      else if (category?.includes('Rest')) violationType = 'Rest Break';
      else if (category?.includes('Break')) violationType = 'Rest Break';
    }
    
    if (violationType && state) {
      return wageLawsLookup[`${state}-${violationType}`];
    }
    return null;
  }, [wageLawsLookup]);

  // Calculate costs
  const calculations = useMemo(() => {
    const results = {
      totalEmployees: 0,
      nonComplianceRisk: { baseDamages: 0, liquidatedDamages: 0, civilPenalties: 0, total: 0 },
      overComplianceCost: { extraPayments: 0, total: 0 },
      byCompany: {},
      byState: {},
      byCategory: {},
      details: []
    };

    if (!payRules.length) return results;

    payRules.forEach(rule => {
      const employeeCount = rule.EmployeeCount || 0;
      const state = rule.State;
      const company = rule.CompanyName;
      const category = rule.Category || "Other";
      const status = rule.ComplianceStatus;

      results.totalEmployees += employeeCount;

      // Initialize buckets
      if (!results.byCompany[company]) {
        results.byCompany[company] = { risk: 0, overpay: 0, employees: 0 };
      }
      if (state && !results.byState[state]) {
        results.byState[state] = { risk: 0, overpay: 0, employees: 0 };
      }
      if (!results.byCategory[category]) {
        results.byCategory[category] = { risk: 0, overpay: 0, count: 0 };
      }

      results.byCompany[company].employees += employeeCount;
      if (state) results.byState[state].employees += employeeCount;
      results.byCategory[category].count += 1;

      const annualHours = hoursPerYear;
      const annualOTHours = annualHours * (overtimePercent / 100);
      const baseAnnualWages = employeeCount * annualHours * hourlyRate;

      const law = getMatchingLaw(state, category, rule.Code);

      if (status === "Non-Compliant") {
        let baseDamage = 0, liquidatedDamage = 0, civilPenalty = 0;

        if (law) {
          const wageMultiplier = parseFloat(law.WageMultiplier) || 1;
          const liquidatedMultiplier = parseFloat(law.LiquidatedDamagesMultiplier) || 0;
          const avgCivilPenalty = ((parseFloat(law.CivilPenaltyMinPerViolation) || 0) + 
                                   (parseFloat(law.CivilPenaltyMaxPerViolation) || 0)) / 2;

          if (law.ViolationType === "Overtime") {
            baseDamage = employeeCount * annualOTHours * hourlyRate * 0.5 * wageMultiplier;
          } else if (law.ViolationType === "DoubleTime") {
            baseDamage = employeeCount * (annualHours * 0.02) * hourlyRate * wageMultiplier;
          } else if (law.ViolationType === "Rest Break" || law.ViolationType === "Meal Break") {
            baseDamage = employeeCount * 52 * hourlyRate * wageMultiplier;
          } else {
            baseDamage = baseAnnualWages * 0.03;
          }

          liquidatedDamage = baseDamage * liquidatedMultiplier;
          civilPenalty = avgCivilPenalty * employeeCount;
        } else {
          baseDamage = baseAnnualWages * 0.03;
        }

        const totalRisk = baseDamage + liquidatedDamage + civilPenalty;
        
        results.nonComplianceRisk.baseDamages += baseDamage;
        results.nonComplianceRisk.liquidatedDamages += liquidatedDamage;
        results.nonComplianceRisk.civilPenalties += civilPenalty;
        results.nonComplianceRisk.total += totalRisk;

        results.byCompany[company].risk += totalRisk;
        if (state) results.byState[state].risk += totalRisk;
        results.byCategory[category].risk += totalRisk;

        results.details.push({
          type: "risk", payRuleId: rule.PayRuleID, company, state, category,
          ruleName: rule.PayRuleName, employees: employeeCount, amount: totalRisk,
          baseDamage, liquidatedDamage, civilPenalty, law, hourlyRate,
          annualOTHours, hoursPerYear: annualHours
        });

      } else if (status === "Over-Compliant") {
        let extraCost = 0;

        if (category.includes("Overtime")) {
          extraCost = employeeCount * (annualOTHours * 0.1) * hourlyRate * 0.5;
        } else if (category.includes("Break")) {
          extraCost = employeeCount * ((10 / 60) * 260) * hourlyRate;
        } else {
          extraCost = baseAnnualWages * 0.01;
        }

        results.overComplianceCost.extraPayments += extraCost;
        results.overComplianceCost.total += extraCost;

        results.byCompany[company].overpay += extraCost;
        if (state) results.byState[state].overpay += extraCost;
        results.byCategory[category].overpay += extraCost;

        results.details.push({
          type: "overpay", payRuleId: rule.PayRuleID, company, state, category,
          ruleName: rule.PayRuleName, employees: employeeCount, amount: extraCost,
          baseDamage: extraCost, liquidatedDamage: 0, civilPenalty: 0, law: null,
          hourlyRate, annualOTHours, hoursPerYear: annualHours
        });
      }
    });

    return results;
  }, [payRules, getMatchingLaw, hoursPerYear, hourlyRate, overtimePercent]);

  // Period multiplier
  const getPeriodMultiplier = useCallback(() => {
    switch (period) {
      case "monthly": return 1 / 12;
      case "quarterly": return 1 / 4;
      default: return 1;
    }
  }, [period]);

  const formatCurrency = useCallback((amount) => {
    const adjusted = amount * getPeriodMultiplier();
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(adjusted);
  }, [getPeriodMultiplier]);

  const getPeriodLabel = useCallback(() => {
    switch (period) {
      case "monthly": return "/month";
      case "quarterly": return "/quarter";
      default: return "/year";
    }
  }, [period]);

  // Chart data
  const companyChartData = useMemo(() => {
    return Object.entries(calculations.byCompany)
      .map(([name, data]) => ({
        name: name.length > 15 ? name.substring(0, 15) + "..." : name,
        fullName: name,
        Risk: data.risk * getPeriodMultiplier(),
        Overpay: data.overpay * getPeriodMultiplier(),
        employees: data.employees
      }))
      .sort((a, b) => (b.Risk + b.Overpay) - (a.Risk + a.Overpay));
  }, [calculations.byCompany, getPeriodMultiplier]);

  const stateChartData = useMemo(() => {
    return Object.entries(calculations.byState)
      .map(([name, data]) => ({
        name,
        Risk: data.risk * getPeriodMultiplier(),
        Overpay: data.overpay * getPeriodMultiplier(),
        employees: data.employees
      }))
      .sort((a, b) => (b.Risk + b.Overpay) - (a.Risk + a.Overpay))
      .slice(0, 10);
  }, [calculations.byState, getPeriodMultiplier]);

  const categoryPieData = useMemo(() => {
    const colors = {
      "Overtime": "#EF4444", "Break Violation": "#F59E0B",
      "Rest Break Violation": "#F97316", "Meal Break Violation": "#FB923C", "Other": "#6B7280"
    };
    return Object.entries(calculations.byCategory)
      .filter(([_, data]) => data.risk > 0 || data.overpay > 0)
      .map(([name, data]) => ({
        name, value: (data.risk + data.overpay) * getPeriodMultiplier(),
        risk: data.risk * getPeriodMultiplier(),
        overpay: data.overpay * getPeriodMultiplier(),
        color: colors[name] || "#8B5CF6"
      }))
      .sort((a, b) => b.value - a.value);
  }, [calculations.byCategory, getPeriodMultiplier]);

  const totalRisk = calculations.nonComplianceRisk.total;
  const totalOverpay = calculations.overComplianceCost.total;
  const grandTotal = totalRisk + totalOverpay;

  // Generate employee breakdown for selected detail
  const generateEmployeeBreakdown = useCallback((detail) => {
    if (!detail) return [];
    
    const employees = [];
    const empHourlyRate = hourlyRate;
    
    for (let i = 1; i <= Math.min(detail.employees, 50); i++) {
      let violationHours = 0, backWages = 0, totalDamages = 0;
      
      if (detail.law) {
        const wageMultiplier = parseFloat(detail.law.WageMultiplier) || 1;
        const liquidatedMultiplier = parseFloat(detail.law.LiquidatedDamagesMultiplier) || 0;
        
        if (detail.law.ViolationType === "Overtime") {
          violationHours = detail.annualOTHours || (detail.hoursPerYear * 0.1);
          backWages = violationHours * empHourlyRate * 0.5 * wageMultiplier;
        } else if (detail.law.ViolationType === "DoubleTime") {
          violationHours = detail.hoursPerYear * 0.02;
          backWages = violationHours * empHourlyRate * wageMultiplier;
        } else if (detail.law.ViolationType === "Rest Break" || detail.law.ViolationType === "Meal Break") {
          violationHours = 52;
          backWages = violationHours * empHourlyRate * wageMultiplier;
        } else {
          violationHours = detail.hoursPerYear * 0.03;
          backWages = violationHours * empHourlyRate;
        }
        
        totalDamages = backWages + (backWages * liquidatedMultiplier);
      } else {
        violationHours = detail.hoursPerYear * 0.03;
        backWages = violationHours * empHourlyRate;
        totalDamages = backWages;
      }
      
      employees.push({
        id: `EMP-${String(i).padStart(4, '0')}`,
        state: detail.state || 'N/A',
        hourlyRate: empHourlyRate,
        violationHours, backWages, total: totalDamages
      });
    }
    
    return employees;
  }, [hourlyRate]);

  // Detail period multiplier
  const getDetailPeriodMultiplier = useCallback(() => {
    switch (detailPeriod) {
      case "monthly": return 1 / 12;
      case "quarterly": return 1 / 4;
      default: return 1;
    }
  }, [detailPeriod]);

  const formatDetailCurrency = useCallback((amount) => {
    const adjusted = amount * getDetailPeriodMultiplier();
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(adjusted);
  }, [getDetailPeriodMultiplier]);

  // Filter options
  const filterOptions = useMemo(() => {
    const states = new Set();
    const categories = new Set();
    calculations.details.forEach(detail => {
      if (detail.state) states.add(detail.state);
      if (detail.category) categories.add(detail.category);
    });
    return { states: Array.from(states).sort(), categories: Array.from(categories).sort() };
  }, [calculations.details]);

  // Filter and sort details
  const filteredDetails = useMemo(() => {
    let filtered = calculations.details;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.ruleName?.toLowerCase().includes(search) || d.company?.toLowerCase().includes(search)
      );
    }
    if (selectedState !== "all") filtered = filtered.filter(d => d.state === selectedState);
    if (selectedType !== "all") filtered = filtered.filter(d => d.type === selectedType);
    if (selectedCategory !== "all") filtered = filtered.filter(d => d.category === selectedCategory);
    
    return filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortConfig.key) {
        case "type": comparison = (a.type || "").localeCompare(b.type || ""); break;
        case "ruleName": comparison = (a.ruleName || "").localeCompare(b.ruleName || ""); break;
        case "company": comparison = (a.company || "").localeCompare(b.company || ""); break;
        case "state": comparison = (a.state || "").localeCompare(b.state || ""); break;
        case "category": comparison = (a.category || "").localeCompare(b.category || ""); break;
        case "employees": comparison = (a.employees || 0) - (b.employees || 0); break;
        case "amount": default: comparison = (a.amount || 0) - (b.amount || 0); break;
      }
      return sortConfig.direction === "desc" ? -comparison : comparison;
    });
  }, [calculations.details, searchTerm, selectedState, selectedType, selectedCategory, sortConfig]);

  const hasActiveFilters = searchTerm || selectedState !== "all" || selectedType !== "all" || selectedCategory !== "all";

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedState("all");
    setSelectedType("all");
    setSelectedCategory("all");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading cost calculator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 animate-fade-in" data-testid="cost-calculator-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight flex items-center gap-2">
            <Calculator className="w-6 h-6 text-primary" />
            Cost Calculator
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Estimate compliance costs and potential savings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Assumptions Bar */}
      <AssumptionsBar
        period={period}
        setPeriod={setPeriod}
        totalEmployees={calculations.totalEmployees}
        hoursPerYear={hoursPerYear}
        setHoursPerYear={setHoursPerYear}
        hourlyRate={hourlyRate}
        setHourlyRate={setHourlyRate}
        overtimePercent={overtimePercent}
        setOvertimePercent={setOvertimePercent}
      />

      {/* Summary Cards */}
      <CostSummaryCards
        grandTotal={grandTotal}
        totalRisk={totalRisk}
        totalOverpay={totalOverpay}
        nonComplianceRisk={calculations.nonComplianceRisk}
        overComplianceCost={calculations.overComplianceCost}
        formatCurrency={formatCurrency}
        getPeriodLabel={getPeriodLabel}
      />

      {/* Charts */}
      <CostCharts
        companyChartData={companyChartData}
        stateChartData={stateChartData}
        categoryPieData={categoryPieData}
        formatCurrency={formatCurrency}
        getPeriodMultiplier={getPeriodMultiplier}
      />

      {/* Details Table */}
      <CostDetailsTable
        filteredDetails={filteredDetails}
        totalDetails={calculations.details.length}
        hasActiveFilters={hasActiveFilters}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        filterOptions={filterOptions}
        clearFilters={clearFilters}
        sortConfig={sortConfig}
        requestSort={requestSort}
        formatCurrency={formatCurrency}
        onRowClick={setSelectedDetail}
      />

      {/* Disclaimer */}
      <p className="text-[10px] text-muted-foreground italic text-center">
        * Estimates based on assumptions above. Actual liability may vary based on specific circumstances, 
        violation frequency, and jurisdiction. Consult legal counsel for accurate assessment.
      </p>

      {/* Detail Modal */}
      <CostDetailModal
        selectedDetail={selectedDetail}
        onClose={() => setSelectedDetail(null)}
        detailPeriod={detailPeriod}
        setDetailPeriod={setDetailPeriod}
        formatDetailCurrency={formatDetailCurrency}
        generateEmployeeBreakdown={generateEmployeeBreakdown}
        hourlyRate={hourlyRate}
      />
    </div>
  );
};

export default CostCalculatorPage;
